import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Search, Download, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

export default function History() {
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Fechas: las del INPUT (lo que el usuario teclea) y las APLICADAS (después de clic en Aplicar)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd, setAppliedEnd] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    department: 'all'
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
    setPage(1);
    // eslint-disable-next-line
  }, [visits, filters, appliedStart, appliedEnd]);

  const loadData = async () => {
    try {
      const [visitsRes, deptsRes, empsRes] = await Promise.all([
        api.getHistory(),
        api.getDepartments(),
        api.getEmployees()
      ]);
      setVisits(visitsRes.data);
      setDepartments(deptsRes.data);
      setEmployees(empsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    }
  };

  const applyFilters = () => {
    let filtered = [...visits];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(v =>
        v.visitor_name.toLowerCase().includes(searchLower) ||
        v.visitor_company.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(v => v.status === filters.status);
    }

    if (filters.department !== 'all') {
      filtered = filtered.filter(v => v.department_id === filters.department);
    }

    // Solo filtro fecha si están APLICADAS (después del clic en Aplicar)
    if (appliedStart) {
      const sd = new Date(appliedStart + 'T00:00:00');
      filtered = filtered.filter(v => new Date(v.entry_datetime) >= sd);
    }
    if (appliedEnd) {
      const ed = new Date(appliedEnd + 'T23:59:59');
      filtered = filtered.filter(v => new Date(v.entry_datetime) <= ed);
    }

    setFilteredVisits(filtered);
  };

  const handleApply = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('La fecha inicio debe ser anterior a la fecha fin');
      return;
    }
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
    toast.success('Filtro aplicado');
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStart('');
    setAppliedEnd('');
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'N/A';
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.name : 'N/A';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending_entry': { label: 'Entrada Pendiente', class: 'bg-orange-100 text-orange-700' },
      'approved_entry': { label: 'Dentro', class: 'bg-green-100 text-green-700' },
      'completed': { label: 'Completado', class: 'bg-gray-100 text-gray-700' }
    };
    const badge = badges[status] || { label: status, class: 'bg-gray-100 text-gray-700' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (appliedStart) params.start_date = appliedStart;
      if (appliedEnd) params.end_date = appliedEnd;
      const response = await axios.get(`${API}/reports/visits-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_visitas_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el reporte');
    }
  };

  // --- Paginación ---
  const totalPages = Math.max(1, Math.ceil(filteredVisits.length / PAGE_SIZE));
  const pageVisits = filteredVisits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const goPrev = () => setPage(Math.max(1, page - 1));
  const goNext = () => setPage(Math.min(totalPages, page + 1));

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="history-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Historial Visitas</h1>
            <p className="text-gray-600 mt-1">Consulte el registro completo de visitantes</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 w-40"
                data-testid="history-start-date"
              />
              <span className="text-gray-500">a</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10 w-40"
                data-testid="history-end-date"
              />
            </div>
            <Button
              onClick={handleApply}
              data-testid="history-apply-button"
              className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Aplicar
            </Button>
            {(appliedStart || appliedEnd) && (
              <Button
                onClick={handleClearDates}
                variant="outline"
                data-testid="history-clear-dates"
                className="font-medium uppercase tracking-wide"
              >
                Limpiar
              </Button>
            )}
            <Button
              onClick={downloadPDF}
              data-testid="download-history-pdf-button"
              className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2"
            >
              <Download className="h-5 w-5" />
              Descargar PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="search-input"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Nombre o empresa..."
                  className="pl-10 h-11"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Estado</label>
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger data-testid="status-filter" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending_entry">Entrada Pendiente</SelectItem>
                  <SelectItem value="approved_entry">Dentro</SelectItem>
                  <SelectItem value="completed">Completado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Departamento</label>
              <Select value={filters.department} onValueChange={(value) => setFilters({ ...filters, department: value })}>
                <SelectTrigger data-testid="department-filter" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {filteredVisits.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No se encontraron registros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visitante</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departamento</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empleado</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entrada</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salida</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageVisits.map((visit) => (
                    <tr key={visit.id} className="hover:bg-gray-50 transition-colors" data-testid="history-row">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{visit.visitor_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{visit.visitor_company}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{getDepartmentName(visit.department_id)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{getEmployeeName(visit.employee_id)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(visit.entry_datetime).toLocaleString('es-ES')}
                        {visit.approved_entry_by && (
                          <div className="text-xs text-gray-400">Por: {visit.approved_entry_by}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {visit.exit_datetime ? (
                          <>
                            {new Date(visit.exit_datetime).toLocaleString('es-ES')}
                            {visit.approved_exit_by && (
                              <div className="text-xs text-gray-400">Por: {visit.approved_exit_by}</div>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">{getStatusBadge(visit.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación + contador */}
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-600">
          <span data-testid="history-counter">
            Mostrando {pageVisits.length} de {filteredVisits.length} registros (Total: {visits.length})
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2" data-testid="history-pagination">
              <Button variant="outline" size="sm" onClick={goPrev} disabled={page === 1} data-testid="history-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2">Página {page} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={goNext} disabled={page === totalPages} data-testid="history-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
